import styled, { css } from 'styled-components';
import { readableColor, darken, lighten } from 'polished';
import { Link } from 'gatsby';

const isExternalLink = (to) => [
  '#',
  'http:',
  'https:',
  'mailto:',
  'tel:',
  'sms:',
  'facetime:',
  'whatsapp:',
].some((external) => to.startsWith(external));

// make sure this component keeps allowing ref.
// gatsby-theme-document uses this with ref

export default styled.button.attrs((props) => {
  if (props.to) {
    if (isExternalLink(props.to)) {
      return {
        as: 'a',
        href: props.to,
        to: undefined,
      };
    }
    return {
      as: Link,
    };
  }
  if (props.href) {
    return {
      as: 'a',
    };
  }
  return {
    as: 'button',
    type: props.type || 'button',
    role: 'button',
  };
}).attrs((props) => {
  if (props.$wrapper || props.$link) {
    return {
      $base: 'none',
      className: `${props.className} button-non-cta`,
    };
  }
  return {
    $base: 'cta',
    className: `${props.className} button-cta`,
  };
}).attrs((props) => {
  if (props.$outlineDark) {
    return {
      className: `${props.className} button-outline-dark`,
    };
  }
  return {};
})`
  transition: .2s;
  background: none;
  border: 0;
  display: inline-flex;
  gap: 0.25rem;
  ${({ $largerGap }) => $largerGap && css`
    gap: 0.5rem;
  `}
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &:not(:disabled) {
    cursor: pointer;
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  svg {
    height: 1rem;
    width: 1rem;
    transition: inherit;
  }

  ${({ $base }) => $base === 'none' && css`
    margin: 0;
    padding: 0;
  `}

  ${({ $link, $linkHoverUnderline, theme }) => $link && css`
    color: ${theme.primaryColor};
    justify-content: flex-start;
    text-decoration: none;

    &:hover {
      ${$linkHoverUnderline && css`
        text-decoration: underline;
      `}
      ${!$linkHoverUnderline && css`
        color: ${darken(0.2, theme.primaryColor)};
      `}
    }

    svg {
      height: 0.75rem;
      width: 0.75rem;
      padding-top: 0.1rem;
    }
  `}

  ${({ $base, theme }) => $base === 'cta' && css`
    padding: .75rem 1rem;
    color: white;
    text-decoration: none;

    background-color: ${theme.primaryColor};
    border: 1px solid ${theme.primaryColor};
    &:hover {
      background-color: ${darken(0.2, theme.primaryColor)};
      border-color: ${darken(0.2, theme.primaryColor)};
    }
  `}

  ${({ $secondary, theme }) => $secondary && css`
    color: ${theme.fontColor};
    background-color: ${theme.secondaryColor || theme.gray01 || 'transparent'};
    border: 1px solid ${theme.secondaryColor || theme.gray01 || 'transparent'};

    &:hover {
      background-color: ${theme.primaryColor};
      border-color: ${theme.primaryColor};
      &,
      svg {
        color: white;
      }
    }
  `}

  ${({ $dark, theme }) => $dark && css`
    background-color: ${theme.grayDarkColor};
    border: 1px solid ${theme.grayDarkColor};
    color: white;

    &:hover {
      background-color: ${darken(0.2, theme.grayDarkColor)};
      border-color: ${darken(0.2, theme.grayDarkColor)};
      color: ${darken(0.2, '#ffffff')};
    }
  `}

  ${({ $outline, theme }) => $outline && css`
    background-color: transparent;
    color: ${theme.primaryColor};

    &:hover {
      background-color: transparent;
      color: ${darken(0.2, theme.primaryColor)};
      border: 1px solid ${darken(0.2, theme.primaryColor)};
    }
  `}

  ${({ $outlineWhite }) => $outlineWhite && css`
    background-color: transparent;
    color: white;
    border-color: white;
    &:hover {
      background-color: transparent;
      color: ${darken(0.2, 'white')};
      border-color: ${darken(0.2, 'white')};
    }
  `}

  ${({ $outlineDark, theme }) => $outlineDark && css`
    background-color: transparent;
    color: ${theme.grayDarkColor};
    border-color: ${theme.grayDarkColor};
    &:hover {
      background-color: transparent;
      color: ${darken(0.2, theme.grayDarkColor)};
      border-color: ${darken(0.2, theme.grayDarkColor)};
    }
  `}

  ${({ $outlineReadable, readableBackgroundColor, theme }) => $outlineReadable && css`
    background-color: transparent;
    color: ${readableColor(readableBackgroundColor || theme.backgroundColor, theme.fontColor, 'white', false)};
    border-color: ${readableColor(readableBackgroundColor || theme.backgroundColor, theme.fontColor, 'white', false)};
    &:hover {
      background-color: transparent;
      color: ${readableColor(readableBackgroundColor || theme.backgroundColor, lighten(0.3, theme.fontColor), darken(0.2, 'white'), false)};
      border-color: ${readableColor(readableBackgroundColor || theme.backgroundColor, lighten(0.3, theme.fontColor), darken(0.2, 'white'), false)};
    }
  `}

  ${({ $standout, theme }) => $standout && theme.standoutColor && css`
    background-color: ${theme.standoutColor};
    border: 1px solid ${theme.standoutColor};
    &:hover {
      background-color: ${darken(0.1, theme.standoutColor)};
      border-color: ${darken(0.1, theme.standoutColor)};
    }
  `}

  ${({ $green }) => $green && css`
    background-color: #008000;
    border: 1px solid #008000;
    &:hover {
      background-color: ${darken(0.1, '#008000')};
      border-color: ${darken(0.1, '#008000')};
    }
  `}

  ${({ $large }) => $large && css`
    font-size: 1.125rem;
  `}

  ${({ $wide }) => $wide && css`
    padding-left: 2rem;
    padding-right: 2rem;
  `}

  ${({ $fullWidth }) => $fullWidth && css`
    width: 100%;

    &:not(:first-child) {
      margin-top: .5rem;
    }
  `}
`;
